import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useMutation as rqUseMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useQuery } from "@apollo/client";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import { FilteredFolders } from "constants/forecasts.constants";
import { ForecastActivityType, TYPE_WELLS } from "constants/settings.constants";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectCheckedForecasts,
  selectCheckedTypeWells,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";

import { FOLDERS, FOLDERS_LIGHT } from "api/userArps";
import {
  Rescat,
  ValNavFolderBulkRequest,
  saveValNavFolderBulk,
  updatePerformanceData
} from "api/userForecasts";
import { fetchTypeWellSegmentsAndLengths } from "api/valnavTypeWellLength";

import { ForecastFolder, ForecastItem, WellData } from "models/UserArpsModel";

import { useSelectedProject } from "components/project/projects/hooks";

import { ArpsSegmentToSegmentDto as arpsSegmentToSegmentDto } from "../../arps/utils/arpsUtils";
import "../context/reducer/arpsReducer";
import { groupArpsSegmentsByProduct } from "../utils/groupArpsSegmentsByProduct";
import useForecastFolderFetcher from "./useForecastFolderFetcher";

let arpsWasm: typeof import("wasm/arps") = undefined;
import("wasm/arps.js").then((x) => {
  arpsWasm = x;
});

export default function useValNavFolderTypeWellUpdater(type: ForecastActivityType) {
  const checkedTypeWells = useForecastStore(selectCheckedTypeWells);
  const checkedForecastsList = useForecastStore(selectCheckedForecasts);
  const checkedForecasts = type === TYPE_WELLS ? checkedTypeWells : checkedForecastsList;

  const filteredTypeWellFolders = useSelector(
    (state: RootState) => state.arps[FilteredFolders.FilteredTypeWellFolders]
  );

  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const setCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);
  const setSyncStateType = useForecastStore((s) => s.setSyncStateType);
  const setSyncStateId = useForecastStore((s) => s.setSyncStateId);

  const { selectedProject: project } = useSelectedProject();
  const { getForecastFoldersFromFoldersData } = useForecastFolderFetcher(type);

  const [valnavError, setValnavError] = useState(null);

  // Use lightweight query for USER_ARPS (forecasts), full query for TYPE_WELLS
  const foldersQuery = type === TYPE_WELLS ? FOLDERS : FOLDERS_LIGHT;

  const { data: foldersData, refetch } = useQuery(foldersQuery, {
    variables: {
      req: {
        projectId: project?.projectId,
        type: type
      }
    },
    skip: !project?.projectId
  });

  const valnavFolderMutation = rqUseMutation(
    "vnfolder",
    (data: ValNavFolderBulkRequest) => {
      return saveValNavFolderBulk(data);
    },
    {
      onSuccess: async (_data, variables) => {
        await refetch();
        // EVA-6855: recalc perf metrics on re-sync, same call the upload path makes.
        // fire-and-forget — a calc failure must not fail the sync (data is already saved),
        // but capture it: a swallowed failure here is a silent recurrence of EVA-6855 (stale metrics).
        updatePerformanceData(variables.folderId).catch((error) => {
          Sentry.withScope((scope) => {
            scope.setContext("updatePerformanceData", { folderId: variables.folderId });
            scope.setLevel("error");
            Sentry.captureException(error);
          });
        });
      }
    }
  );

  function getAllChildFolders(parentFolderId: string): ForecastFolder[] {
    const children = filteredTypeWellFolders.filter((f) => f.parentId === parentFolderId);

    const allChildren: ForecastFolder[] = [...children];

    children.forEach((child) => {
      const grandchildren = getAllChildFolders(child.folderId);
      allChildren.push(...grandchildren);
    });

    return allChildren;
  }

  function recheckAllForecasts(foldersData: ForecastFolder[]) {
    const newChecked = [...checkedForecasts];

    foldersData.forEach((folder) => {
      folder.children.forEach((child) => {
        const index = newChecked.findIndex(
          (f) => f.uniqueID === child.uniqueID && f.folderId === child.folderId
        );
        if (index >= 0) {
          newChecked.splice(index, 1, child as ICheckedForecast);
        }
      });
    });

    setCheckedGlobalTypeWells(newChecked.filter(Boolean));
    //checkedforecast is immutable here because dispatch above
    if (type === TYPE_WELLS) {
      setCheckedTypeWells(newChecked);
      setCheckedTypeWellKeys(newChecked.map((x) => x.id));
    } else {
      setCheckedForecasts(newChecked);
      setCheckedForecastKeys(newChecked.map((x) => x.id));
    }
  }

  async function updateFolderWithValNavData(
    folder: ForecastFolder,
    modifyingSelectedTypeWell: boolean = false
  ): Promise<boolean> {
    if (!IS_INTERNAL_ENV || type !== TYPE_WELLS) {
      return false;
    }

    const forecasts: ForecastItem[] = [];

    for (let i = 0; i < folder.children.length; i++) {
      const source = folder.children[i].source;
      if (!source) {
        continue;
      }
      const rescat = (folder.children[i].reserveCategory ??
        folder.reserveCategory) as Rescat;
      const result = await fetchTypeWellSegmentsAndLengths(source, rescat, [
        folder.children[i].title
      ]);
      const { lengths, segments, constants } = result;

      const manuallyOverrideDi = [];
      if (arpsWasm) {
        for (const key of Object.keys(segments)) {
          const arpsGroupedByProduct = groupArpsSegmentsByProduct(segments[key]);
          Object.keys(arpsGroupedByProduct).forEach((product) => {
            const productSegments = arpsGroupedByProduct[product];
            const segmentDtos = arpsSegmentToSegmentDto(arpsWasm, productSegments);
            const result = arpsWasm.calculateDi(segmentDtos);
            if (result.length == productSegments.length) {
              for (let i = 0; i < result.length; i++) {
                const currentDi = productSegments[i].di;
                const calculatedDi = result[i].di;
                const diffDiPercent =
                  Math.abs(Math.abs(currentDi - calculatedDi) / currentDi) * 100.0;
                if (diffDiPercent > 1) {
                  // Same objects as in segments[key], so this updates the payload.
                  productSegments[i].di = calculatedDi;
                  manuallyOverrideDi.push(
                    `${key}: ${currentDi.toFixed(8)} -> ${calculatedDi.toFixed(8)}`
                  );
                }
              }
            }
          });
        }
        //let user know that Di has been recalculated
        if (manuallyOverrideDi.length > 0) {
          toast.warning(
            <div>
              Detected Di that differs from the database: <br />
              {manuallyOverrideDi.join(", ")}
            </div>
          );
        }
      }
      const folderName = folder.folderName;
      const foundFolder = foldersData.folders.filter((f) => f.name === folderName);
      // Index lengths by this well's key (same key used for segments/constants
      // below) so the Hz Length always matches the well being imported. See EVA-6612.
      let wellData: WellData = {
        HzLength: lengths[folder.children[i].title] ?? null,
        Proppant: null,
        Stage: null
      } as WellData;

      if (foundFolder.length > 0) {
        for (const fc of foundFolder[0].forecasts) {
          if (!wellData?.Proppant && !wellData?.Stage && !wellData?.HzLength) {
            wellData = fc.wellData;
          }
        }
      }

      const forecastItem: ForecastItem = {
        id: folder.children[i].id,
        name: folder.children[i].title,
        uniqueID: folder.children[i].uniqueID,
        source: source,
        wellData: wellData,
        folderId: folder.folderId,
        arps: segments[folder.children[i].title],
        constants: constants[folder.children[i].title],
        reserveCategory: rescat,
        wellListRecords: []
      };

      forecasts.push(forecastItem);
    }

    const typeWells = (folder.children ?? []).map((tw) => tw.title);

    // When modifying the selected type well, send over old type well to make sure we update the existing and
    // not create new type well.
    const oldTypeWells =
      modifyingSelectedTypeWell &&
      selectedTypeWell &&
      typeWells.length &&
      selectedTypeWell.title !== typeWells[0]
        ? [selectedTypeWell.title]
        : [];

    const request: ValNavFolderBulkRequest = {
      folderId: folder.folderId,
      projectId: project?.projectId,
      oldForecasts: oldTypeWells,
      forecasts
    };
    await valnavFolderMutation.mutateAsync(request);
    return true;
  }
  async function onUpdateValNavFolder(folder?: ForecastFolder) {
    try {
      setSyncStateType("folder");
      setSyncStateId(folder?.folderId || null);

      if (!folder) return;

      let childFolders = getAllChildFolders(folder.folderId);

      childFolders = [folder, ...childFolders];

      for (const f of childFolders) {
        const updateOk = await updateFolderWithValNavData(f);
        if (!updateOk) {
          setValnavError(`Unable to refresh folder '${f.folderName}' with valnav.`);
          continue;
        }
      }
      //update forecast folders
      const result = await refetch({
        req: {
          projectId: project?.projectId,
          type: type
        }
      });

      if (!result?.data) {
        setValnavError("Unable to refresh folders with valnav.");
        return;
      }
      const foldersData = getForecastFoldersFromFoldersData(result?.data, type);

      recheckAllForecasts(foldersData);
    } catch (err) {
      setValnavError(err?.data ?? "An error occurred.");
    } finally {
      setSyncStateType(null);
      setSyncStateId(null);
    }
  }

  return {
    onUpdateValNavFolder,
    updateFolderWithValNavData,
    valnavError,
    setValnavError
  };
}
